/* eslint-disable no-underscore-dangle, object-shorthand */
const getUKTimeInSeconds = require('./utils').getUKTimeInSeconds;
const uiSettings = require('./utils/UISettings.js');

const State = {
  CLOSED: 'CLOSED',
  OPEN: 'OPEN',
  CLOSING_SOON: 'CLOSING_SOON',
  OPENING_SOON: 'OPENING_SOON',
  NO_DATA: 'NO_DATA'
};

const DeliveryType = {
  PAY_AND_COLLECT: 'PAY_AND_COLLECT',
  RESERVE_AND_COLLECT: 'RESERVE_AND_COLLECT',
  NO_DELIVERY_TYPE: 'NO_DELIVERY_TYPE',
  PAY_AND_COLLECT_FROM_STORE_STOCK: 'PAY_AND_COLLECT_FROM_STORE_STOCK'
};

let CHANGING_SOON_THRESHOLD;

/** @see @link{http://confluence.dixons.co.uk/display/DBS/Final} */
/** @constructor */
/** @param {object} data */
function StoreAvailability(data) {
  CHANGING_SOON_THRESHOLD = Math.max.apply(null, uiSettings.get('stores')['cutoff-time']);
  this.__data = data;
  // Time functionalities depend on current date
  // but the data not always contains currentDate member so we need to provide it.
  this.__data.currentDate = this.__data.currentDate || new Date();
}

StoreAvailability.prototype = {
  /** @returns {string} */
  getCollectionLabel: function() {
    return this.__data.buttonLabel || '';
  },

  /** @returns {string} */
  getAvailabilityType: function() {
    return this.__data.deliveryType;
  },

  /** @returns {boolean} */
  isOutOfStock: function() {
    return this.__data.stockType === 'OUT_OF_STOCK';
  },
  /** @returns {boolean} */
  isInStock: function() {
    return this.__data.stockType === 'GOOD_STOCK' ||
      this.__data.stockType === 'IN_STOCK' ||
      this.__data.stockType === 'STORE_STOCK';
  },
  /** @returns {string} */
  isPickUp: function() {
    return this.__data.pickUp;
  },

  isOnDisplay: function() {
    return this.__data.onDisplay;
  },

  /** @returns {string} */
  getStockAvailability: function() {
    return this.__data.stockType;
  },

  /** @returns {string}  */
  getTradeMark: function() {
    let tradeMark;
    if (this.__data.storeLabel.match(/pc world/i)) {
      tradeMark = 'PCWORLD';
    } else if (this.__data.storeLabel.match(/currys/i)) {
      tradeMark = 'CURRYS';
    } else if (this.__data.storeLabel.match(/carphone/i)) {
      tradeMark = 'CARPHONE';
    }
    return tradeMark;
  },

  /** @returns {string} */
  getStockAvailabilityLabel: function() {
    return this.__data.stockLabel;
  },

  /** @returns {string} */
  getStoreId: function() {
    return this.__data.storeId || 0;
  },

  /** @returns {string} */
  getStoreName: function() {
    return this.__data.storeLabel || '';
  },

  /** @returns {Array} */
  getStoreOpeningHours: function() {
    return this.__data.openHours || [];
  },

  /** @returns {string} */
  getStoreAddress: function() {
    return this.__data.description || '';
  },

  /** @returns {string} */
  getStoreDistance: function() {
    return this.__data.distance || '';
  },

  getStoreUrl: function() {
    return this.__data.mapLink || '';
  },

  getStoreUrlLabel: function() {
    return this.__data.mapLink || '';
  },

  /** @returns {string} */
  getCountry: function() {
    return this.__data.country || '';
  },

  getOpenState: function() {
    const currentTime = this.getMinutesFromStartOfDay();
    const todayOpeningHours = this.__data.openHours[0];

    if (!todayOpeningHours) {
      return State.NO_DATA;
    } if (isClosingSoon(currentTime, todayOpeningHours)) {
      return State.CLOSING_SOON;
    } if (isOpen(currentTime, todayOpeningHours)) {
      return State.OPEN;
    } if (isOpeningSoon(currentTime, todayOpeningHours)) {
      return State.OPENING_SOON;
    }
    return State.CLOSED;
  },

  /**
   * Returns next open day object if it exists. if not, this method returns null
   *
   * @returns {null|Object}
   */
  getNextOpenDay: function() {
    const currentTime = this.getMinutesFromStartOfDay();

    const offset = this.__data.openHours.findIndex(
      (day, i) => (i === 0 && (currentTime < day.openMinutes || isOpen(currentTime, day))) ||
        (i !== 0 && typeof day.openMinutes === 'number')
    );
    const openDay = this.__data.openHours[offset] || null;

    return openDay && ({ ...openDay, offset: offset });
  },

  getTimeUntilClosed: function() {
    const todayOpeningHours = this.__data.openHours[0];
    return todayOpeningHours && (todayOpeningHours.closeMinutes - this.getMinutesFromStartOfDay());
  },

  getTimeUntilOpen: function() {
    const todayOpeningHours = this.__data.openHours[0];
    return todayOpeningHours && (todayOpeningHours.openMinutes - this.getMinutesFromStartOfDay());
  },

  getMinutesFromStartOfDay: function() {
    return (Math.floor(getUKTimeInSeconds(this.__data.currentDate) / 60)) % (60 * 24);
  }
};

function isOpen(time, dayOpeningHours) {
  return time >= dayOpeningHours.openMinutes && time < dayOpeningHours.closeMinutes;
}

function isClosingSoon(time, dayOpeningHours) {
  return isOpen(time, dayOpeningHours) &&
    time >= dayOpeningHours.closeMinutes - CHANGING_SOON_THRESHOLD;
}

function isOpeningSoon(time, dayOpeningHours) {
  return time >= dayOpeningHours.openMinutes - CHANGING_SOON_THRESHOLD &&
    time < dayOpeningHours.openMinutes;
}

module.exports = {
  StoreAvailability: StoreAvailability,
  State: State,
  DeliveryType: DeliveryType
};
